import {
  ACTUALLY_SUPPORTED_LANGUAGES,
  type ActuallySupportedLanguage,
} from '@splitfiesta/shared';

export interface SupportedLanguageInfo {
  code: string;
  name: string;
  nativeName: string;
  rtl?: boolean;
}

export type SupportedLanguage =
  | 'en'
  | 'fr'
  | 'es'
  | 'de'
  | 'pt'
  | 'it'
  | 'nl'
  | 'pl'
  | 'ru'
  | 'zh'
  | 'ja'
  | 'ko'
  | 'ar'
  | 'hi'
  | 'tr'
  | 'vi'
  | 'id'
  | 'th'
  | 'sv'
  | 'da'
  | 'fi'
  | 'no'
  | 'cs'
  | 'hu'
  | 'ro'
  | 'el'
  | 'he'
  | 'fa'
  | 'ur'
  | 'bn'
  | 'ta'
  | 'te';

export const SUPPORTED_LANGUAGES: SupportedLanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', rtl: true },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', rtl: true },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', rtl: true },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български' },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski' },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
];

// The supported-language list moved to `@splitfiesta/shared` (apps/api's
// `moduleResolution: node` cannot read this package's `exports` subpaths);
// re-exported here so this package's public API is unchanged.
export { ACTUALLY_SUPPORTED_LANGUAGES };
export type { ActuallySupportedLanguage };

// Centralized language configuration for all external integrations
export const LANGUAGE_INTEGRATIONS = {
  // Date-fns locale mappings
  dateFns: {
    en: null, // Default/built-in
    fr: 'fr',
    es: 'es',
    de: 'de',
    pt: 'pt',
  },
  // Emoji Mart locale mappings
  emojiMart: {
    en: 'en',
    fr: 'fr',
    es: 'es',
    de: 'de',
    pt: 'pt',
  },
  // API validation schema
  validation: ACTUALLY_SUPPORTED_LANGUAGES,
  // ML model language codes
  mlModels: ACTUALLY_SUPPORTED_LANGUAGES,
} as const;

export const DEFAULT_LANGUAGE = 'en';
export const FALLBACK_LANGUAGE = 'en';
export const NAMESPACE_SEPARATOR = ':';
export const KEY_SEPARATOR = '.';

export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'groups',
  'expenses',
  'import',
  'settlements',
  'notifications',
  'validation',
  'landing',
  'activity',
  'currencies',
  'balances',
  'members',
  'friends',
  'profile',
  'settings',
  'app',
  'emails',
  'timezones',
  'reports',
  'social',
  'invitations',
  'claimLink',
  'errors',
  'pages',
  'admin',
  'mobile',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export interface I18nConfig {
  defaultLanguage: string;
  fallbackLanguage: string;
  supportedLanguages: string[];
  namespaces: string[];
  interpolation: {
    escapeValue: boolean;
  };
  react: {
    useSuspense: boolean;
  };
}

export function isRTL(language: string): boolean {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === language);
  return lang?.rtl || false;
}

export function getLanguageByCode(
  code: string
): SupportedLanguageInfo | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

export function getSupportedLanguageCodes(): string[] {
  return SUPPORTED_LANGUAGES.map(lang => lang.code);
}

export function isLanguageSupported(code: string): boolean {
  // Only check against languages we actually have translations for
  return ACTUALLY_SUPPORTED_LANGUAGES.includes(
    code as ActuallySupportedLanguage
  );
}

export function getNearestSupportedLanguage(code: string): string {
  // Direct match
  if (isLanguageSupported(code)) {
    return code;
  }

  // Language family match (e.g., 'en-US' -> 'en')
  const baseLanguage = code.split('-')[0];
  if (isLanguageSupported(baseLanguage)) {
    return baseLanguage;
  }

  // Fallback to default
  return DEFAULT_LANGUAGE;
}

export function getEnabledLanguageInfo(): SupportedLanguageInfo[] {
  // A–Z by native name — every language menu (web header, web Settings)
  // shows this list, and the mobile picker hard-codes the same order.
  return SUPPORTED_LANGUAGES.filter(lang =>
    ACTUALLY_SUPPORTED_LANGUAGES.includes(
      lang.code as ActuallySupportedLanguage
    )
  ).sort((a, b) => a.nativeName.localeCompare(b.nativeName));
}
